import React, { useEffect, useRef, useState } from "react";
import { DEBUG_ENABLED } from "../helpers/debugEnabled";

type Props = {
  videoEl: HTMLVideoElement | null;
  videoStream: MediaStream | null;
  getDrawPreviewMsAgo?: () => number;
};

type Snapshot = {
  rvfcTotal: number;
  rvfcRatePerSec: number;
  rvfcMsSinceLast: number;
  decodedFps: number;
  currentTime: number;
  readyState: number;
  paused: boolean;
  trackState: string;
  trackMuted: boolean;
  longtasksLast5s: number;
  longestLongtaskMs: number;
  longtaskRatePerSec: number;
  visibility: string;
  rafRatePerSec: number;
  drawPreviewMsAgo: number;
};

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  top: 8,
  left: 8,
  background: "rgba(0,0,0,0.75)",
  color: "#0f0",
  fontFamily: "monospace",
  fontSize: "10px",
  lineHeight: "1.3",
  padding: "6px 8px",
  borderRadius: "4px",
  zIndex: 99999,
  pointerEvents: "none",
  maxWidth: "180px",
};

const labelStyle: React.CSSProperties = { color: "#ff0" };

export const LiveSignals: React.FC<Props> = ({ videoEl, videoStream, getDrawPreviewMsAgo }) => {
  const rvfcTotalRef = useRef(0);
  const rvfcLastTsRef = useRef(0);
  const rvfcWindowRef = useRef<number[]>([]);
  const decodedSamplesRef = useRef<{ ts: number; count: number }[]>([]);
  const longtasksRef = useRef<{ ts: number; dur: number }[]>([]);
  const rafWindowRef = useRef<number[]>([]);
  const rafCancelRef = useRef<boolean>(false);

  const [snap, setSnap] = useState<Snapshot>({
    rvfcTotal: 0,
    rvfcRatePerSec: 0,
    rvfcMsSinceLast: 0,
    decodedFps: 0,
    currentTime: 0,
    readyState: 0,
    paused: true,
    trackState: "?",
    trackMuted: false,
    longtasksLast5s: 0,
    longestLongtaskMs: 0,
    longtaskRatePerSec: 0,
    visibility: typeof document !== "undefined" ? document.visibilityState : "?",
    rafRatePerSec: 0,
    drawPreviewMsAgo: -1,
  });

  // requestVideoFrameCallback — fires only when a NEW decoded frame is
  // presented. If this stops ticking during a "freeze" → camera/decoder
  // stalled. If it keeps ticking but our preview is stale → our draw
  // pipeline (RAF loop, drawImage) is the failure point.
  useEffect(() => {
    if (!videoEl) return;
    const v = videoEl as any;
    if (typeof v.requestVideoFrameCallback !== "function") return;

    let cancelled = false;
    const tick = (_now: number, _metadata: any) => {
      if (cancelled) return;
      const t = performance.now();
      rvfcTotalRef.current += 1;
      rvfcLastTsRef.current = t;
      rvfcWindowRef.current.push(t);
      const cutoff = t - 1000;
      while (
        rvfcWindowRef.current.length &&
        rvfcWindowRef.current[0] < cutoff
      ) {
        rvfcWindowRef.current.shift();
      }
      v.requestVideoFrameCallback(tick);
    };
    v.requestVideoFrameCallback(tick);
    return () => {
      cancelled = true;
    };
  }, [videoEl]);

  // PerformanceObserver for longtasks. >50ms blocks the main thread enough
  // to drop RAF callbacks. If freezes correlate with longtasks, our code
  // (or some other JS on the page) is jamming the thread.
  useEffect(() => {
    if (typeof PerformanceObserver === "undefined") return;
    let observer: PerformanceObserver | null = null;
    try {
      observer = new PerformanceObserver((entries) => {
        const now = performance.now();
        for (const entry of entries.getEntries()) {
          longtasksRef.current.push({ ts: now, dur: entry.duration });
        }
      });
      observer.observe({ type: "longtask", buffered: false });
    } catch {
      // Some browsers reject longtask observation; ignore.
    }
    return () => {
      if (observer) observer.disconnect();
    };
  }, []);

  // Bare RAF rate — independent of rVFC. If RAF rate stays high but rVFC
  // drops, decoder is stalled. If RAF drops too, main thread is stuck.
  useEffect(() => {
    rafCancelRef.current = false;
    const rafTick = () => {
      if (rafCancelRef.current) return;
      const t = performance.now();
      rafWindowRef.current.push(t);
      const cutoff = t - 1000;
      while (rafWindowRef.current.length && rafWindowRef.current[0] < cutoff) {
        rafWindowRef.current.shift();
      }
      requestAnimationFrame(rafTick);
    };
    requestAnimationFrame(rafTick);
    return () => {
      rafCancelRef.current = true;
    };
  }, []);

  // Visibility / page-lifecycle change logger — written to console so it
  // shows up in remote DevTools too.
  useEffect(() => {
    const onVis = () => {
      console.log(
        `[LiveSignals] visibility=${document.visibilityState} at ${Date.now()}`
      );
    };
    const onPageHide = () => console.log("[LiveSignals] pagehide");
    const onPageShow = () => console.log("[LiveSignals] pageshow");
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, []);

  // 4Hz snapshot loop — recompute display values and re-render
  useEffect(() => {
    const interval = setInterval(() => {
      const now = performance.now();

      const v = videoEl as any;
      const quality =
        v && typeof v.getVideoPlaybackQuality === "function"
          ? v.getVideoPlaybackQuality()
          : null;
      const decodedCount =
        quality?.totalVideoFrames ?? v?.webkitDecodedFrameCount ?? 0;
      decodedSamplesRef.current.push({ ts: now, count: decodedCount });
      const cutoff1s = now - 1000;
      while (
        decodedSamplesRef.current.length > 1 &&
        decodedSamplesRef.current[0].ts < cutoff1s
      ) {
        decodedSamplesRef.current.shift();
      }
      const oldest = decodedSamplesRef.current[0];
      const decodedFps = oldest ? decodedCount - oldest.count : 0;

      const cutoff5s = now - 5000;
      longtasksRef.current = longtasksRef.current.filter(
        (lt) => lt.ts >= cutoff5s
      );
      const longestLt = longtasksRef.current.reduce(
        (m, lt) => Math.max(m, lt.dur),
        0
      );
      const cutoff1sLt = now - 1000;
      const ltLast1s = longtasksRef.current.filter(
        (lt) => lt.ts >= cutoff1sLt
      ).length;

      const track = videoStream?.getVideoTracks?.()[0] ?? null;

      const drawPreviewMsAgo = getDrawPreviewMsAgo ? getDrawPreviewMsAgo() : -1;

      setSnap({
        rvfcTotal: rvfcTotalRef.current,
        rvfcRatePerSec: rvfcWindowRef.current.length,
        rvfcMsSinceLast: rvfcLastTsRef.current
          ? Math.round(now - rvfcLastTsRef.current)
          : -1,
        decodedFps,
        currentTime: videoEl?.currentTime ?? 0,
        readyState: videoEl?.readyState ?? 0,
        paused: videoEl?.paused ?? true,
        trackState: track?.readyState ?? "?",
        drawPreviewMsAgo,
        trackMuted: track?.muted ?? false,
        longtasksLast5s: longtasksRef.current.length,
        longestLongtaskMs: Math.round(longestLt),
        longtaskRatePerSec: ltLast1s,
        visibility: document.visibilityState,
        rafRatePerSec: rafWindowRef.current.length,
      });
    }, 250);
    return () => clearInterval(interval);
  }, [videoEl, videoStream, getDrawPreviewMsAgo]);

  if (!DEBUG_ENABLED) return null;

  // Highlight bad numbers so the freeze cause jumps out visually
  const rvfcColor =
    snap.rvfcRatePerSec < 5
      ? "#f33"
      : snap.rvfcRatePerSec < 20
      ? "#fa0"
      : "#0f0";
  const decodedColor =
    snap.decodedFps < 5 ? "#f33" : snap.decodedFps < 20 ? "#fa0" : "#0f0";
  const rafColor =
    snap.rafRatePerSec < 30
      ? "#f33"
      : snap.rafRatePerSec < 50
      ? "#fa0"
      : "#0f0";
  const ltColor =
    snap.longestLongtaskMs > 200
      ? "#f33"
      : snap.longestLongtaskMs > 80
      ? "#fa0"
      : "#0f0";
  const drawPreviewColor =
    snap.drawPreviewMsAgo < 0 || snap.drawPreviewMsAgo > 200
      ? "#f33"
      : snap.drawPreviewMsAgo > 80
      ? "#fa0"
      : "#0f0";

  return (
    <div style={overlayStyle}>
      <div>
        <span style={labelStyle}>rVFC</span>{" "}
        <span style={{ color: rvfcColor }}>{snap.rvfcRatePerSec}/s</span>{" "}
        ({snap.rvfcMsSinceLast}ms ago)
      </div>
      <div>
        <span style={labelStyle}>decoded</span>{" "}
        <span style={{ color: decodedColor }}>{snap.decodedFps}fps</span>
      </div>
      <div>
        <span style={labelStyle}>raf</span>{" "}
        <span style={{ color: rafColor }}>{snap.rafRatePerSec}/s</span>
      </div>
      <div>
        <span style={labelStyle}>cT</span> {snap.currentTime.toFixed(2)}s rs=
        {snap.readyState} p={snap.paused ? "Y" : "N"}
      </div>
      <div>
        <span style={labelStyle}>trk</span> {snap.trackState} m=
        {snap.trackMuted ? "Y" : "N"}
      </div>
      <div>
        <span style={labelStyle}>LT5s</span>{" "}
        <span style={{ color: ltColor }}>
          {snap.longtasksLast5s} max={snap.longestLongtaskMs}ms
        </span>
      </div>
      <div>
        <span style={labelStyle}>drawP</span>{" "}
        <span style={{ color: drawPreviewColor }}>
          {snap.drawPreviewMsAgo < 0 ? "—" : `${snap.drawPreviewMsAgo}ms ago`}
        </span>
      </div>
      <div>
        <span style={labelStyle}>vis</span> {snap.visibility}
      </div>
    </div>
  );
};
